/** @jsxImportSource @emotion/react */
import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiSkeletonText,
  EuiSpacer,
  EuiTitle,
  useEuiTheme,
} from "@inscopix/ideas-eui";
import { Project } from "graphql/_Types";
import {
  USER_ACCESS_LEVELS_BY_ID,
  USER_ACCESS_LEVELS_BY_KEY,
} from "types/UserAccessLevels";
import { getProjectDjango } from "django/libraryProjectDjango";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "django/queryKeys";
import { getPermissionData } from "components/ProjectSharing/ProjectSharing";
import { permissions } from "components/ProjectSharing/permissions";
import { RoleBadge } from "components/RoleBadge/RoleBadge";
import { groupBy } from "lodash";
import { UserAvatar } from "components/UserAvatar/UserAvatar";
import { isDefined } from "utils/isDefined";
import { css } from "@emotion/react";
import { ButtonIconPermissioned } from "components/ButtonIconPermissioned/ButtonIconPermissioned";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";

export interface PanelAccessProps {
  projectId: Project["id"];
  goToAccessTab: () => void;
}

/*
 * Project page sidebar panel with sharing/access information
 */
export const PanelAccess = ({ projectId, goToAccessTab }: PanelAccessProps) => {
  const { data, isLoading, isRefetchError, isLoadingError } = useQuery({
    queryFn: () =>
      getPermissionData(
        projectId,
        getProjectDjango,
        permissions.userFunctions.internalAccess
          .getInternalUsersWithProjectAccess,
        permissions.userFunctions.externalAccess
          .getProjectSharingExternalInvitations,
      ),
    queryKey: queryKeys.modalProjectShare(projectId),
    refetchOnMount: "always",
  });

  const projectOwner = useDataTableContext((s) => s.project.user?.id);

  const { euiTheme } = useEuiTheme();
  const styles = {
    dl: css`
      display: grid;
      grid-template-columns: max-content auto;
    `,
    dt: css`
      grid-column-start: 1;
      margin-right: 0.5rem;
      font-weight: bold;
      padding: 5px 0px;
    `,
    dd: css`
      padding: 5px 0px;
    `,
    subContent: css`
      color: ${euiTheme.colors.subduedText};
    `,
  };

  if (isLoading) {
    return <EuiSkeletonText lines={5} />;
  }

  if (data === undefined || isRefetchError || isLoadingError) {
    return null;
  }

  const defaultAccessLevel = data.project.default_user_access_level;
  const defaultAccessRole = USER_ACCESS_LEVELS_BY_ID[defaultAccessLevel].name;

  const usersWithAccessByLevel = groupBy(
    data.usersWithProjectAccess,
    (user) => user.access_level,
  );
  const adminsWithAccess =
    usersWithAccessByLevel[USER_ACCESS_LEVELS_BY_KEY["ADMIN"].id];
  const editorsWithAccess =
    usersWithAccessByLevel[USER_ACCESS_LEVELS_BY_KEY["EDITOR"].id];
  const viewersWithAccess =
    usersWithAccessByLevel[USER_ACCESS_LEVELS_BY_KEY["COPIER"].id];

  const externalUsersWithAccess = data.externalUsersWithAccess;

  return (
    <EuiPanel
      hasBorder={false}
      hasShadow={false}
      paddingSize="s"
      style={{ flexGrow: "0" }}
    >
      <EuiFlexGroup responsive={false}>
        <EuiFlexItem>
          <EuiTitle size="s">
            <span>Access</span>
          </EuiTitle>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <ButtonIconPermissioned
            aria-label="Edit access"
            requiredPermission="grant_access"
            iconType="pencil"
            onClick={goToAccessTab}
          />
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer size="s" />
      <dl css={styles.dl}>
        <dt css={styles.dt}>Owner</dt>
        <dd css={styles.dd}>
          {projectOwner !== undefined && (
            <UserAvatar userId={projectOwner} size="s" />
          )}
        </dd>
        <dt css={styles.dt}>Organization</dt>
        <dd css={styles.dd}>
          <RoleBadge role={defaultAccessRole} />
        </dd>
        {isDefined(adminsWithAccess) && adminsWithAccess.length > 0 && (
          <>
            <dt css={styles.dt}>Admin</dt>
            <dd css={styles.dd}>
              <EuiFlexGroup gutterSize={"s"} wrap={true} responsive={false}>
                {adminsWithAccess.map((user) => (
                  <EuiFlexItem key={user.user} grow={false}>
                    <UserAvatar userId={user.user} size="s" />
                  </EuiFlexItem>
                ))}
              </EuiFlexGroup>
            </dd>
          </>
        )}
        {isDefined(editorsWithAccess) && editorsWithAccess.length > 0 && (
          <>
            <dt css={styles.dt}>Editor</dt>
            <dd css={styles.dd}>
              <EuiFlexGroup gutterSize={"s"} wrap={true} responsive={false}>
                {editorsWithAccess.map((user) => (
                  <EuiFlexItem key={user.user} grow={false}>
                    <UserAvatar userId={user.user} size="s" />
                  </EuiFlexItem>
                ))}
              </EuiFlexGroup>
            </dd>
          </>
        )}
        {isDefined(viewersWithAccess) && viewersWithAccess.length > 0 && (
          <>
            <dt css={styles.dt}>Viewer</dt>
            <dd css={styles.dd}>
              <EuiFlexGroup gutterSize={"s"} wrap={true} responsive={false}>
                {viewersWithAccess.map((user) => (
                  <EuiFlexItem key={user.user} grow={false}>
                    <UserAvatar userId={user.user} size="s" />
                  </EuiFlexItem>
                ))}
              </EuiFlexGroup>
            </dd>
          </>
        )}
        {isDefined(externalUsersWithAccess) &&
          externalUsersWithAccess.length > 0 && (
            <>
              <dt css={styles.dt}>External Users</dt>
              <dd css={styles.dd}>
                <EuiFlexGroup gutterSize={"s"} wrap={true} responsive={false}>
                  {externalUsersWithAccess.map((user) => (
                    <EuiFlexItem key={user.id} grow={false}>
                      <UserAvatar userId={user.id} size="s" />
                    </EuiFlexItem>
                  ))}
                </EuiFlexGroup>
              </dd>
            </>
          )}
      </dl>
    </EuiPanel>
  );
};
